/**
 * FormModal Component
 *
 * Reusable modal wrapper for forms. Wraps Modal so it inherits focus trap,
 * Escape handling, body-scroll lock, and overlay click.
 */

import { FormEvent, ReactNode } from 'react';
import Modal from './Modal';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  title: string;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  error?: string | null;
  size?: 'sm' | 'md' | 'lg';
}

export default function FormModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  isSubmitDisabled = false,
  error = null,
  size = 'md',
}: FormModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size} labelledBy="form-modal-title">
      <form onSubmit={onSubmit}>
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 id="form-modal-title" className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}
          {children}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isSubmitDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
